/**
 * PATH: apps/quote/src/components/builder/panels/context-panel/FilePreview.tsx
 * PURPOSE: In-panel file preview with zoom controls for images and PDFs
 */

import { useState } from 'react'
import { ArrowLeft, ZoomIn, ZoomOut, Maximize2, Download, Link as LinkIcon } from 'lucide-react'
import { type QuoteAttachment } from '@/lib/quoteApi'
import { getFileIcon, formatFileSize } from './helpers'

interface FilePreviewProps {
  file: QuoteAttachment
  onBack: () => void
  onOpenExternal: () => void
  onDownload: () => void
  onLinkToRow?: () => void
}

export default function FilePreview({ file, onBack, onOpenExternal, onDownload, onLinkToRow }: FilePreviewProps) {
  const [zoom, setZoom] = useState(100)
  const isImage = file.file_type?.startsWith('image/') || false
  const isPDF = file.file_type === 'application/pdf' || file.original_filename.endsWith('.pdf')

  return (
    <div className="flex flex-col h-full">
      {/* Preview header */}
      <div className="flex items-center justify-between p-2 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={onBack} className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-800 rounded" title="Back to list">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
              {file.title || file.original_filename}
            </p>
            <p className="text-[10px] text-slate-500">{formatFileSize(file.file_size)}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {isImage && (
            <>
              <button onClick={() => setZoom(Math.max(50, zoom - 25))} disabled={zoom <= 50} className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-800 rounded disabled:opacity-30" title="Zoom out">
                <ZoomOut className="w-3.5 h-3.5" />
              </button>
              <span className="text-xs text-slate-500 w-10 text-center">{zoom}%</span>
              <button onClick={() => setZoom(Math.min(200, zoom + 25))} disabled={zoom >= 200} className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-800 rounded disabled:opacity-30" title="Zoom in">
                <ZoomIn className="w-3.5 h-3.5" />
              </button>
            </>
          )}
          {onLinkToRow && (
            <button onClick={onLinkToRow} className="p-1.5 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded text-blue-600" title="Link to selected row">
              <LinkIcon className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={onOpenExternal} className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-800 rounded" title="Open in new tab">
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
          <button onClick={onDownload} className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-800 rounded" title="Download">
            <Download className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Preview content */}
      <div className="flex-1 overflow-auto bg-slate-100 dark:bg-slate-950 flex items-center justify-center p-2">
        {isImage && file.file_url ? (
          <img
            src={file.file_url}
            alt={file.title || file.original_filename}
            className="max-w-full h-auto transition-transform"
            style={{ transform: `scale(${zoom / 100})` }}
          />
        ) : isPDF && file.file_url ? (
          <iframe
            src={`${file.file_url}#toolbar=0&navpanes=0`}
            className="w-full h-full border-0 rounded"
            title={file.title || file.original_filename}
          />
        ) : (
          <div className="text-center">
            {getFileIcon(file.file_type, file.original_filename)}
            <p className="text-sm text-slate-500 mt-2">Preview not available</p>
            <button onClick={onOpenExternal} className="mt-3 px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">
              Open in new tab
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
